import type { EventIn, IntakeLineProposal, IntakeProposeIn } from "./api";

export type InvoiceLine = { sku: string; invoicedQty: string; unitPrice: string };

/** Line value at 2 dp: invoiced qty x invoice unit price. */
export function lineValue(line: InvoiceLine): string {
  return (Number(line.invoicedQty) * Number(line.unitPrice)).toFixed(2);
}

export function buildPropose(entity: "LTD" | "OU", invoiceRef: string, freightTotal: string, lines: InvoiceLine[]): IntakeProposeIn {
  return {
    entity,
    invoice_ref: invoiceRef.trim(),
    freight_total: freightTotal.trim() === "" ? "0.00" : Number(freightTotal).toFixed(2),
    lines: lines.filter((line) => line.sku && line.invoicedQty.trim() !== "").map((line) => ({ sku: line.sku, invoiced_qty: line.invoicedQty.trim(), line_value: lineValue(line) })),
  };
}

export function toActualQty(invoicedQty: string, invoiceMultiplier: string): string {
  return (Number(invoicedQty) * Number(invoiceMultiplier)).toFixed(4);
}

export function adjustShare(lines: IntakeLineProposal[], lineValues: string[], index: number, share: string, freightTotal: string): IntakeLineProposal[] {
  const total = Number(freightTotal);
  const fixed = Math.min(Math.max(Number(share), 0), total);
  const others = lines.map((_, lineIndex) => lineIndex).filter((lineIndex) => lineIndex !== index);
  const otherValue = others.reduce((sum, lineIndex) => sum + Number(lineValues[lineIndex]), 0);
  const remaining = total - fixed;
  const next = lines.map((line) => ({ ...line }));
  next[index].freight_share = fixed.toFixed(2);
  let assigned = 0;
  others.forEach((lineIndex, position) => {
    const raw = otherValue === 0 ? remaining / others.length : (remaining * Number(lineValues[lineIndex])) / otherValue;
    const cents = position === others.length - 1 ? Math.round((remaining - assigned) * 100) : Math.floor(raw * 100);
    assigned += cents / 100;
    next[lineIndex].freight_share = (cents / 100).toFixed(2);
  });
  for (const line of next) line.inbound_cost_per_unit = Number(line.qty) === 0 ? "0.000000" : (Number(line.freight_share) / Number(line.qty)).toFixed(6);
  return next;
}

export type IntakeConfirm = {
  entity: "LTD" | "OU"; invoiceRef: string; invoiceDate: string; factory: string; purchaseId: string | null;
  adjusted: boolean; qc: Record<string, "pending" | "passed" | "failed" | "not_required">;
};

export function buildIntakeEvents(confirm: IntakeConfirm, lines: IntakeLineProposal[], ids: string[], occurredAt: string): EventIn[] {
  return lines.map((line, index) => ({
    event_id: ids[index], event_type: "purchase_intake", entity: confirm.entity, sku: line.sku, factory: confirm.factory,
    qty: line.qty, unit_value_applied: line.unit_value, inbound_cost_applied: line.inbound_cost_per_unit,
    invoice_ref: confirm.invoiceRef, purchase_id: confirm.purchaseId, qc_flag: confirm.qc[line.sku] ?? "not_required",
    freight_split: { freight_share: line.freight_share, method: confirm.adjusted ? "adjusted" : "value_proportional" },
    occurred_at: occurredAt, reference: confirm.invoiceRef,
  }));
}

export function earlierIntake(rows: Array<{ event_type: string; invoice_ref: string | null; is_voided: boolean; occurred_at: string }>, invoiceRef: string): string | null {
  const hit = rows.find((row) => row.event_type === "purchase_intake" && !row.is_voided && row.invoice_ref === invoiceRef.trim());
  return hit ? hit.occurred_at.slice(0, 10) : null;
}

export function latestCountAt(rows: Array<{ event_type: string; is_voided: boolean; occurred_at: string }>): string | null {
  const counts = rows.filter((row) => !row.is_voided && (row.event_type === "opening_balance" || row.event_type === "stock_take_adjustment"));
  if (counts.length === 0) return null;
  return counts.map((row) => row.occurred_at.slice(0, 10)).sort().at(-1) ?? null;
}

export function backdated(invoiceDate: string, latestCount: string | null): boolean {
  return latestCount !== null && invoiceDate < latestCount;
}
